// The single blade-segment collision routine shared by mouse, touch, and camera hands
// (CLAUDE.md §4). During the Duplicate-Storm mash and the Mega-Ticket finale the rest of
// the world is frozen and only the mash target is hittable.
import { G } from './state';
import { MEGA_CAP, POM_CAP, POM_PTS } from './config';
import type { Entity } from './types';
import { addScore, gameOver, mashCenter, resolveTicket, sliceChute, startMash } from './scoring';
import { addText, shred } from '../fx/effects';
import { sfx, buzz } from '../fx/audio';

// Does the segment (x1,y1)->(x2,y2) come within the entity's radius? Hitboxes are 1.3×
// in camera mode because air-swiping is less precise.
export function segHit(x1: number, y1: number, x2: number, y2: number, o: { x: number; y: number; r: number }): boolean {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const l2 = dx * dx + dy * dy;
  let u = l2 ? ((o.x - x1) * dx + (o.y - y1) * dy) / l2 : 0;
  u = Math.max(0, Math.min(1, u));
  const rr = o.r * (G.camMode ? 1.3 : 1);
  const px = x1 + u * dx - o.x;
  const py = y1 + u * dy - o.y;
  return px * px + py * py < rr * rr;
}

export function bladeSegment(x1: number, y1: number, x2: number, y2: number): void {
  if (G.state === 'play' && G.mash) {
    // Duplicate Storm mash — rest of the game is frozen
    const c = mashCenter();
    G.pomCool -= 1;
    // No cap — slice as many as physically possible (only the 5s timer limits it). Cooldown
    // of 2 frames (~30/s ceiling, above human mash speed) keeps a single overlap from counting
    // every frame without throttling a fast hand.
    if (G.pomCool <= 0 && segHit(x1, y1, x2, y2, c)) {
      G.pomCool = 2;
      G.mash.hits++;
      addScore(POM_PTS);
      sfx.mash();
      buzz(8);
      shred(c.x + (Math.random() - 0.5) * c.r, c.y + (Math.random() - 0.5) * c.r, '#8e4ec6', 3);
      addText(c.x + (Math.random() - 0.5) * c.r, c.y - c.r * 0.75, 'dupe merged ×' + G.mash.hits + '  +' + POM_PTS, '#7c3aed');
      // milestone flourish for clearing all 50 — still rewarded even though slicing continues
      if (G.mash.hits === POM_CAP) {
        addScore(10);
        addText(G.W / 2, G.H * 0.3, 'ZERO DUPLICATES! +10', '#8e4ec6', true);
        sfx.combo();
      }
    }
    return;
  }
  if (G.state === 'play') {
    for (const o of G.objs) {
      if (o.dead) continue;
      if (segHit(x1, y1, x2, y2, o)) {
        if (o.kind === 'ticket') resolveTicket(o, false);
        else if (o.kind === 'angry') {
          if (!o.happy) {
            o.dead = true;
            addText(o.x, o.y, 'ESCALATION!', '#dc2626', true);
            shred(o.x, o.y, '#dc2626', 30);
            gameOver('angry');
          }
        } else if (o.kind === 'chute') sliceChute(o);
        else if (o.kind === 'pom') {
          startMash(o);
          break;
        }
      }
    }
  } else if (G.state === 'finale') {
    G.megaCool -= 1;
    const m: Entity | { x: number; y: number; r: number } = { x: G.W / 2, y: G.H * 0.45, r: Math.min(G.W, G.H) * 0.22 };
    if (G.megaHits < MEGA_CAP && G.megaCool <= 0 && segHit(x1, y1, x2, y2, m)) {
      G.megaCool = 3;
      G.megaHits++;
      addScore(1);
      sfx.mash();
      shred(m.x + (Math.random() - 0.5) * m.r, m.y + (Math.random() - 0.5) * m.r, '#cfd3da', 3);
      addText(m.x + (Math.random() - 0.5) * m.r, m.y - m.r * 0.6, 'reply deleted ×' + G.megaHits, '#15803d');
    }
  }
}
